import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Info } from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
} from "recharts";

const PURPLE = "#5B2383";
const GOLD = "#D9B300";

const STATUS_TONE = {
  "Completo": "bg-emerald-100 text-emerald-700",
  "Parcial": "bg-amber-100 text-amber-700",
  "No publicado": "bg-slate-100 text-slate-500",
  "Duración por verificar": "bg-orange-100 text-orange-700",
};

const PctBar = ({ label, value, testid }) => (
  <div data-testid={testid}>
    <div className="flex items-center justify-between text-xs">
      <span className="text-slate-600">{label}</span>
      <span className="font-black text-purple-800">{value === null || value === undefined ? "—" : `${value}%`}</span>
    </div>
    <div className="h-2 bg-slate-100 rounded overflow-hidden mt-1">
      <div className="h-full rounded" style={{ width: `${value || 0}%`, backgroundColor: PURPLE }} />
    </div>
  </div>
);

export const ScoutingParticipation = ({ playerId, seasons }) => {
  const withPart = (seasons || []).filter((s) => s.participation);
  const [season, setSeason] = useState(withPart.length ? withPart[withPart.length - 1].season : "");
  const [matches, setMatches] = useState([]);

  useEffect(() => {
    if (!season) return;
    api.get(`/scouting-market/players/${playerId}/matches?season=${encodeURIComponent(season)}`)
      .then((r) => setMatches(r.data || []))
      .catch(() => setMatches([]));
  }, [season, playerId]);

  if (!withPart.length) return null;
  const p = (withPart.find((s) => s.season === season) || withPart[withPart.length - 1]).participation;

  const evo = matches.map((m) => ({
    j: `J${m.jornada ?? "?"}`,
    Minutos: m.minutes_est ?? 0,
    publicado: m.minutes_est !== null && m.minutes_est !== undefined,
  }));
  const possible = p.match_duration && p.matches_played ? p.match_duration * p.matches_played : null;
  const minutesNum = typeof p.minutes_est === "number" ? p.minutes_est : null;

  return (
    <Card className="border-slate-200 shadow-sm" data-testid="participation-section">
      <CardContent className="p-4 space-y-4">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="text-xs uppercase tracking-widest text-slate-500 font-bold">
            Minutos y participación RFFM
          </div>
          <div className="flex items-center gap-2">
            <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${STATUS_TONE[p.minutes_status] || "bg-slate-100 text-slate-500"}`} data-testid="participation-status">
              {p.minutes_status}
            </span>
            <select
              value={season}
              onChange={(e) => setSeason(e.target.value)}
              className="h-8 rounded-md border border-slate-200 bg-white px-2 text-xs font-bold"
              data-testid="participation-season-select"
            >
              {withPart.map((s) => <option key={s.season} value={s.season}>{s.season}</option>)}
            </select>
          </div>
        </div>

        <div className="grid grid-cols-3 sm:grid-cols-7 gap-2 text-center">
          {[
            ["Minutos est.", typeof p.minutes_est === "number" ? p.minutes_est : p.minutes_est],
            ["Partidos", p.matches_played],
            ["Titular", p.starts],
            ["Suplente", p.subs],
            ["Convocado", p.callups],
            ["Min/partido", p.avg_min_per_match ?? "—"],
            ["Min/convoc.", p.avg_min_per_callup ?? "—"],
          ].map(([label, v]) => (
            <div key={label} className="rounded-lg border border-slate-200 p-2">
              <div className="text-lg font-black text-purple-900" style={{ fontFamily: "Nunito" }}>{v ?? "—"}</div>
              <div className="text-[9px] uppercase tracking-widest text-slate-500">{label}</div>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
          <PctBar label="% Titularidades" value={p.pct_starts} testid="pct-starts" />
          <PctBar label={`% Partidos disputados (equipo: ${p.team_matches_reviewed} revisados)`} value={p.pct_matches} testid="pct-matches" />
          <PctBar label="% Participación sobre convocatorias" value={p.pct_participation_callups} testid="pct-callups" />
          <PctBar label="% Minutos en partidos disputados" value={p.pct_minutes_played} testid="pct-minutes" />
          <PctBar label="% Minutos sobre el total del equipo" value={p.pct_minutes_team} testid="pct-minutes-team" />
          {minutesNum !== null && possible && (
            <div className="text-xs text-slate-600 self-end" data-testid="minutes-vs-possible">
              Minutos disputados vs posibles: <b className="text-purple-800">{minutesNum}</b> / {possible}
              {p.match_duration ? ` (${p.match_duration}' por partido · ${p.competition || ""})` : ""}
            </div>
          )}
        </div>

        {evo.length > 0 && (
          <div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-1">
              Evolución de minutos · {season}
            </div>
            <ResponsiveContainer width="100%" height={160}>
              <BarChart data={evo}>
                <XAxis dataKey="j" tick={{ fontSize: 9 }} interval={Math.max(0, Math.floor(evo.length / 20))} />
                <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
                <Tooltip />
                <Bar dataKey="Minutos" fill={PURPLE} radius={[2, 2, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}

        <div className="flex items-start gap-2 rounded-md border p-2.5 text-[11px] text-slate-600" style={{ borderColor: GOLD, backgroundColor: "#D9B30010" }}>
          <Info className="h-3.5 w-3.5 shrink-0 mt-0.5" style={{ color: GOLD }} />
          Los minutos son estimaciones calculadas a partir de las sustituciones y eventos publicados en las actas
          oficiales de la RFFM. Algunas categorías o partidos pueden no disponer de información suficiente.
        </div>
      </CardContent>
    </Card>
  );
};

export default ScoutingParticipation;
